import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import CreativesCard from "../../../ui/CreativesCard";
import { useCreatives } from "../../../../context/CreativeContext";
import { ReactComponent as FileUpload } from "../../../../assets/icons/svg/file-upload.svg";
import { ReactComponent as All } from "../../../../assets/icons/svg/all.svg";
import { ReactComponent as Landscape } from "../../../../assets/icons/svg/landscape.svg";
import { ReactComponent as Protrait } from "../../../../assets/icons/svg/Protrait.svg";
import { ReactComponent as UploadCreative } from "../../../../assets/icons/svg/upload-creative.svg";
import { useTopBar } from "../../../../context/TopBarContext";

type FilterType = "all" | "landscape" | "portrait";

const CreativesListScreen: React.FC = () => {
  const { creatives } = useCreatives();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<FilterType>("all");
  const [search, setSearch] = useState("");
  const { setTitle, setShowBackButton, pushNavState } = useTopBar();
  const location = useLocation();

  useEffect(() => {
    const title = "Creative";
    const showBack = false;

    setTitle(title);
    setShowBackButton(showBack);

    pushNavState({
      path: location.pathname,
      title,
      showBackButton: showBack,
    });
  }, [location.pathname]);

  const filteredCreatives = creatives.filter((creative) => {
    const matchesSearch = creative.title
      ?.toLowerCase()
      .includes(search.toLowerCase());

    const matchesFilter =
      filter === "all" ||
      (filter === "landscape" && creative.orientation === "landscape") ||
      (filter === "portrait" && creative.orientation === "portrait");

    return matchesSearch && matchesFilter;
  });

  // Function to handle upload navigation with direct popup
  const handleUploadCreative = () => {
    navigate("/creatives/upload?direct=true");
  };

  return (
    <div className=" min-h-screen bg-white py-4 pl-6 pr-2 mt-1">
      <div className="mx-auto">
        {/* Top Bar */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-sm 2xl:text-base 4xl:text-lg font-medium text-gray-900">
            Uploaded {creatives.length} creatives
          </h2>

          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4  3xl:w-6 3xl:h-6" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search content"
                className="w-64 pl-10 pr-4 3xl:pl-12 py-2.5 rounded-[8px] border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 placeholder:text-sm   placeholder:font-medium"
              />
            </div>

            <button
              onClick={handleUploadCreative}
              className="inline-flex items-center justify-center gap-2 bg-[#0091AF] hover:bg-[#007B8A] text-white px-4 py-2.5 rounded-md font-xs"
            >
              <FileUpload className="w-4 h-4" />
              Upload creative
            </button>

            <div className="flex gap-1 ml-1 text-gray-600">
              <div
                className={`p-2 rounded-md cursor-pointer ${
                  filter === "all"
                    ? "bg-[#F2FAFD] text-[#0090AD]"
                    : "hover:text-black"
                }`}
                onClick={() => setFilter("all")}
              >
                <All className="w-5 h-5" />
              </div>
              <div
                className={`p-2 rounded-md cursor-pointer ${
                  filter === "landscape"
                    ? "bg-[#F2FAFD] text-[#0090AD]"
                    : "hover:text-black"
                }`}
                onClick={() => setFilter("landscape")}
              >
                <Landscape className="w-5 h-5" />
              </div>
              <div
                className={`p-2 rounded-md cursor-pointer ${
                  filter === "portrait"
                    ? "bg-[#F2FAFD] text-[#0090AD]"
                    : "hover:text-black"
                }`}
                onClick={() => setFilter("portrait")}
              >
                <Protrait className="w-5 h-5" />
              </div>
            </div>
          </div>
        </div>

        {/* Filtered Content */}
        {filteredCreatives.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="text-gray-400 text-center">
              <div className="w-24 h-24 mx-auto mb-4   rounded-full bg-gray-100 flex items-center justify-center">
                <UploadCreative className="w-44 h-44 text-gray-500 -mb-3 " />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No creatives found
              </h3>
              <p className="text-gray-500 mb-4">
                Try uploading or changing filters
              </p>
              <button
                onClick={handleUploadCreative}
                className="inline-flex items-center justify-center gap-2 bg-[#0091AF] hover:bg-[#007B8A] text-white px-3 py-2 rounded-md font-xs"
              >
                <FileUpload className="w-4 h-4" />
                Upload creative
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 2xl:grid-cols-4 3xl:grid-cols-4  4xl:grid-cols-5 gap-6 justify-items-center">
            {filteredCreatives.map((creative) => (
              <CreativesCard key={creative.id} creative={creative} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CreativesListScreen;
